import { DYNAMIC_REPLY_REASONS } from './action-log-constants';
import { formatConfidence, isScore } from './action-log-format';
import type { SocialActionLogRow } from './types';

/**
 * What a confidence score MEANS, for the panel that shows it.
 *
 * This file used to hold a whole verdict vocabulary — headlines, tones, stripe colours, outcome
 * sentences, a confidence meter — built for `action-log-row-details.tsx`. That component was
 * rendered by nothing but its own test suite, and both were deleted on 2026-08-27 along with
 * everything here that only it reached. What survives is the one function the panel people
 * actually see calls.
 */

/** Both numbers, or nothing. A meter drawn against an assumed threshold misreports every shortfall. */
const confidenceMeterValues = (row: SocialActionLogRow): { reached: number; required: number } | null =>
	isScore(row.matchedConfidence) && isScore(row.riskThreshold)
		? { reached: row.matchedConfidence, required: row.riskThreshold }
		: null;

/** Did the match clear the floor? Null when the row cannot answer, which is not the same as "no". */
const clearedGate = (row: SocialActionLogRow): boolean | null => {
	const values = confidenceMeterValues(row);
	return values ? values.reached >= values.required : null;
};

/**
 * Above this, Confirm answered YES; below it, NO.
 *
 * The score is P(YES) at the answer token, so the halfway mark is not a tuning knob — it is where
 * the model's own answer flips, and the only place a sentence can switch between "answered no" and
 * "agreed" without misreporting what happened.
 */
const CONFIRM_ANSWERED_YES_ABOVE = 0.5;

/**
 * What the score MEANS, for a match that fell short.
 *
 * The numbers alone say a rule needed 70% and got 0% without ever saying that a second stage looked
 * at the comment and disagreed — so a reader sees a rule the system chose and then scored at zero,
 * and reasonably concludes it contradicted itself. It did not: Match nominates every rule a comment
 * could satisfy and is told ranking is not its job, and Confirm then asks the stricter question
 * about the winner alone.
 *
 * Only for a match that MISSED the floor. A score that cleared it needs no explanation, and a row
 * whose threshold was never recorded cannot say whether it missed — in both cases this stays silent
 * rather than narrating a verdict it cannot derive.
 *
 * Two sentences rather than one because a single one cannot be true at both ends: at 0% the model
 * answered no outright, while a 92% under a 95% floor is a model that agreed and still fell short.
 */
export const confirmShortfallSentence = (row: SocialActionLogRow): string | null => {
	if (!isScore(row.matchedConfidence) || clearedGate(row) !== false) return null;

	return row.matchedConfidence < CONFIRM_ANSWERED_YES_ABOVE
		? 'The confirmation check re-read the comment against the rule and answered no.'
		: 'The confirmation check agreed, but not firmly enough to clear the floor.';
};

// ── dynamic reply ────────────────────────────────────────────────────────────────

/**
 * A reply Kim wrote for this comment and the agent then held back.
 *
 * `replyThreshold` is what marks the reply as one Kim wrote: it is recorded whenever a generated
 * reply was judged, and never on a rule's fixed copy.
 */
export const isUnsentDraft = (row: SocialActionLogRow): boolean =>
	row.decision === 'blocked' && Boolean(row.responseText?.trim()) && isScore(row.replyThreshold);

/** What the panel says about a dynamic reply that is not simply posted. */
export interface DynamicReplyNotice {
	/** `info` while Kim is still writing; `danger` once nothing is going to be sent. */
	tone: 'info' | 'danger';
	title: string;
	body: string;
	/** Kim's reasoning, or what went wrong. Null when the row says nothing more. */
	why: string | null;
}

const REPLY_NOT_SENT = 'Reply not sent';
const NO_REPLY_WRITTEN = 'No reply written';
const REPLY_SCREEN_PREFIX = 'reply_blocked:';
const KILL_SWITCH_STOPPED_REPLY = 'kill_switch_during_delay';

/**
 * The failure codes worth a sentence. An unmapped code is shown as it is — a code nobody named
 * yet is still information, and hiding it would make a new one invisible.
 */
const FAILURE_SENTENCES: Record<string, string> = {
	approval_required: 'The reply needed an action that requires approval.',
	empty_reply: 'Kim returned an empty reply.',
	blocked_intent: 'This brand blocks the intent of this comment.',
	ai_disabled: 'Kim AI is turned off for this brand.'
};

const failureSentence = (error: string | null | undefined): string | null => {
	const code = error?.trim();
	return code ? (FAILURE_SENTENCES[code] ?? code) : null;
};

const textOrNull = (value: string | null | undefined): string | null => value?.trim() || null;

/** Held on its score. Both numbers come from the row: the floor is the brand's own setting. */
const heldOnScoreNotice = (row: SocialActionLogRow): DynamicReplyNotice => {
	const needed = isScore(row.replyThreshold) ? ` A reply needs ${formatConfidence(row.replyThreshold)}.` : '';
	return {
		tone: 'danger',
		title: REPLY_NOT_SENT,
		body: isScore(row.replyConfidence)
			? `Kim's confidence in this reply was ${formatConfidence(row.replyConfidence)}.${needed}`
			: 'Kim gave this reply no confidence score, so it was not sent.',
		why: textOrNull(row.replyConfidenceReason)
	};
};

/** Held after it cleared its score: the reply screen or the kill switch stopped it. */
const heldAfterScoreNotice = (row: SocialActionLogRow, reason: string): DynamicReplyNotice | null => {
	if (!isUnsentDraft(row)) return null;

	if (reason.startsWith(REPLY_SCREEN_PREFIX)) {
		const finding = reason.slice(REPLY_SCREEN_PREFIX.length).trim();
		return {
			tone: 'danger',
			title: REPLY_NOT_SENT,
			body: finding ? `The reply ${finding}.` : 'The reply did not pass the public reply check.',
			why: null
		};
	}

	if (reason === KILL_SWITCH_STOPPED_REPLY) {
		return {
			tone: 'danger',
			title: REPLY_NOT_SENT,
			body: 'The kill switch was turned on before it went out.',
			why: null
		};
	}

	return null;
};

const queuedNotice = (reason: string): DynamicReplyNotice | null => {
	if (reason === DYNAMIC_REPLY_REASONS.awaiting) {
		return {
			tone: 'info',
			title: 'Kim is writing the reply',
			body: 'It is posted only if Kim is confident enough.',
			why: null
		};
	}

	if (reason === DYNAMIC_REPLY_REASONS.timedOut) {
		return {
			tone: 'danger',
			title: NO_REPLY_WRITTEN,
			body: 'Kim did not answer in time, so nothing was sent.',
			why: null
		};
	}

	return null;
};

const blockedNotice = (row: SocialActionLogRow, reason: string): DynamicReplyNotice | null => {
	switch (reason) {
		case DYNAMIC_REPLY_REASONS.belowThreshold:
			return heldOnScoreNotice(row);
		case DYNAMIC_REPLY_REASONS.failed:
			return {
				tone: 'danger',
				title: NO_REPLY_WRITTEN,
				body: 'Kim could not write a reply.',
				why: failureSentence(row.error)
			};
		case DYNAMIC_REPLY_REASONS.notRequested:
			return {
				tone: 'danger',
				title: NO_REPLY_WRITTEN,
				body: 'Kim was not asked to write a reply.',
				why: failureSentence(row.error)
			};
		default:
			return heldAfterScoreNotice(row, reason);
	}
};

/**
 * What to tell the reader about a dynamic reply, or null when the row needs no notice — a fixed
 * reply, a dynamic reply that was posted, or one still waiting out its delay.
 *
 * Switches on the two fields the server sets, `decision` and `reason`. Nothing is re-derived:
 * whether the score cleared the floor was decided on the server, against the brand's own setting.
 */
export const dynamicReplyNotice = (row: SocialActionLogRow): DynamicReplyNotice | null => {
	const reason = row.reason?.trim();
	if (!reason) return null;

	if (row.decision === 'queued') return queuedNotice(reason);
	if (row.decision === 'blocked') return blockedNotice(row, reason);
	return null;
};

